import { NavigationStyles } from '@generated/graphql';
import { Slide, useMediaQuery } from '@mui/material';
import { UserContext } from '@providers/userContextProvider';
import {
  type AppBarProps,
  Box,
  Collapse,
  AppBar as HephaestusAppBar,
  IconButton,
  navTransitionDuration,
  navTransitionEasing,
  Stack,
  useTheme,
} from 'hephaestus/web';
import { Menu as MenuIcon } from 'hephaestus/webIcons';
import { useContext } from 'react';
import { usePermission } from '../../../hooks/usePermission';
import { AppMenu } from './AppMenu';
import { Avatar } from './Avatar';
import { Notifications } from './Notifications';
import { OrganizationLogo } from './OrganizationLogo';
import { OrganizationSwitcher } from './OrganizationSwitcher';

export type AppBarProp = {
  toggleMobileDrawer?: () => void;
  sx: AppBarProps['sx'];
};

export const AppBar = ({ toggleMobileDrawer, sx }: AppBarProp) => {
  const {
    userContext,
    applicableDowntimeSchedule,
    organizationLoading,
    activeOrganization,
  } = useContext(UserContext);
  const theme = useTheme();
  const isDesktop = useMediaQuery(`(min-width:${theme.breakpoint.lg})`, {
    noSsr: true,
  });
  const canReadNotification = usePermission('read:notification');

  const isUnifiedClientExperience =
    userContext.role?.navigationStyle === NavigationStyles.Simple;
  const hideMobileMenuButton =
    !!applicableDowntimeSchedule || isUnifiedClientExperience;
  const hideAppBarActions = !!applicableDowntimeSchedule;

  if (!activeOrganization.id && organizationLoading) {
    return <HephaestusAppBar.Skeleton sx={sx} />;
  }

  return (
    <HephaestusAppBar sx={sx}>
      <Stack direction='row' sx={{ alignItems: 'center' }}>
        {!hideMobileMenuButton && (
          <Collapse
            in={!isDesktop}
            orientation='horizontal'
            timeout={navTransitionDuration}
            easing={{
              enter: navTransitionEasing,
              exit: navTransitionEasing,
            }}
            appear={false}
          >
            <Box sx={{ display: 'flex', mr: 1 }}>
              <Slide
                direction='right'
                timeout={navTransitionDuration}
                easing={{
                  enter: navTransitionEasing,
                  exit: navTransitionEasing,
                }}
                in={!isDesktop}
                appear={false}
              >
                <IconButton
                  aria-label='menu'
                  size='small'
                  onClick={toggleMobileDrawer}
                >
                  <MenuIcon fontSize='inherit' />
                </IconButton>
              </Slide>
            </Box>
          </Collapse>
        )}
        <OrganizationSwitcher>
          <OrganizationLogo />
        </OrganizationSwitcher>
      </Stack>
      <Stack
        direction='row'
        spacing={0.5}
        useFlexGap
        sx={{ alignItems: 'center' }}
      >
        {canReadNotification && !hideAppBarActions && <Notifications />}
        <AppMenu showOnlyLogout={hideAppBarActions}>
          <Avatar />
        </AppMenu>
      </Stack>
    </HephaestusAppBar>
  );
};
